import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

const articleDetailImages: Record<string, string> = {
  'article-cheng91-entrance': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20digital%20platform%20entrance%20gateway%20concept%2C%20warm%20amber%20and%20cream%20gradient%20background%2C%20clean%20geometric%20shapes%2C%20soft%20lighting%2C%20abstract%20technology%20atmosphere%2C%20professional%20yet%20approachable%20aesthetic%2C%20high%20quality%20editorial%20photography%20with%20smooth%20textures&width=1200&height=600&seq=article-cheng91-entrance&orientation=landscape',
  'article-register-guide': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20registration%20form%20interface%20design%20concept%2C%20warm%20cream%20and%20soft%20amber%20color%20palette%2C%20modern%20UI%20elements%20with%20rounded%20corners%2C%20professional%20digital%20product%20photography%20style%2C%20soft%20diffused%20lighting%2C%20welcoming%20atmosphere&width=1200&height=600&seq=article-register-guide&orientation=landscape',
  'article-member-benefits': 'https://readdy.ai/api/search-image?query=Elegant%20membership%20and%20VIP%20concept%20visual%2C%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20luxury%20aesthetic%2C%20abstract%20geometric%20crown%20shapes%2C%20soft%20gradient%20background%2C%20premium%20branding%20photography%20style%2C%20refined%20atmosphere&width=1200&height=600&seq=article-member-benefits&orientation=landscape',
  'article-platform-features': 'https://readdy.ai/api/search-image?query=Modern%20digital%20platform%20dashboard%20interface%20concept%2C%20warm%20amber%20and%20cream%20color%20scheme%2C%20clean%20minimalist%20UI%20design%2C%20soft%20abstract%20shapes%2C%20professional%20technology%20photography%2C%20smooth%20gradients%2C%20organized%20layout&width=1200&height=600&seq=article-platform-features&orientation=landscape',
  'article-login-fix': 'https://readdy.ai/api/search-image?query=Minimalist%20login%20screen%20troubleshooting%20concept%2C%20warm%20cream%20and%20soft%20amber%20background%2C%20clean%20interface%20elements%2C%20help%20and%20support%20theme%2C%20professional%20digital%20product%20photography%2C%20soft%20diffused%20lighting%2C%20approachable%20aesthetic&width=1200&height=600&seq=article-login-fix&orientation=landscape',
  'article-security-guide': 'https://readdy.ai/api/search-image?query=Digital%20security%20and%20protection%20concept%2C%20warm%20amber%20and%20cream%20gradient%20background%2C%20abstract%20shield%20and%20lock%20geometric%20shapes%2C%20minimalist%20design%2C%20professional%20atmosphere%2C%20soft%20lighting%2C%20trustworthy%20aesthetic&width=1200&height=600&seq=article-security-guide&orientation=landscape',
  'article-summer-event': 'https://readdy.ai/api/search-image?query=Vibrant%20summer%20celebration%20and%20event%20promotion%20concept%2C%20warm%20golden%20amber%20and%20cream%20tones%2C%20festive%20yet%20minimalist%20aesthetic%2C%20abstract%20geometric%20shapes%2C%20soft%20gradient%20background%2C%20joyful%20atmosphere%2C%20professional%20branding%20photography&width=1200&height=600&seq=article-summer-event&orientation=landscape',
  'article-speed-tips': 'https://readdy.ai/api/search-image?query=Fast%20digital%20connection%20and%20speed%20optimization%20concept%2C%20warm%20amber%20and%20cream%20gradient%2C%20abstract%20data%20flow%20geometric%20shapes%2C%20minimalist%20technology%20aesthetic%2C%20soft%20lighting%2C%20dynamic%20yet%20clean%20composition&width=1200&height=600&seq=article-speed-tips&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://cheng91.com/article/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: '成91官方平台',
    },
  };

  return (
    <>
      <PageSEO
        title={article.title}
        description={article.excerpt}
        keywords={`成91,${article.categoryName},${article.title}`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: article.categoryName, to: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-sm font-medium hover:bg-accent-100 transition-colors"
              >
                {article.categoryName}
              </Link>
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>

            <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
              <img
                src={articleDetailImages[article.imageSeq]}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm text-foreground-500">标签：</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">成91</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">{article.categoryName}</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">使用指南</span>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-200">
                <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="p-4 rounded-lg bg-background-100 hover:bg-background-200/50 transition-colors cursor-pointer"
                    >
                      <h4 className="text-sm font-medium text-foreground-900 mb-1 line-clamp-2 hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{related.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
    </>
  );
}